const PROFILE_STORAGE_KEY = 'taskeo.user.profile.preferences';
const DEFAULT_LOCALE = 'pt-BR';
const DEFAULT_TIME_ZONE = 'America/Sao_Paulo';

interface UserProfilePreferences {
  timezone?: string | null;
}

export interface ApiUtcFormatOptions {
  locale?: string;
  timeZone?: string | null;
  intl?: Intl.DateTimeFormatOptions;
  fallback?: string;
}

export function parseApiUtcTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = normalizeApiUtcValue(value);
  if (!normalized) return null;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function resolveSessionTimeZone(): string {
  const fromProfile = readSessionTimeZoneFromProfile();
  if (isValidTimeZone(fromProfile)) return fromProfile;

  const fromBrowser = readBrowserTimeZone();
  if (isValidTimeZone(fromBrowser)) return fromBrowser;

  return DEFAULT_TIME_ZONE;
}

export function formatApiUtcTimestamp(
  value: string | null | undefined,
  options: ApiUtcFormatOptions = {}
): string {
  const date = parseApiUtcTimestamp(value);
  const fallback = options.fallback ?? '-';
  if (!date) return fallback;

  const locale = options.locale || DEFAULT_LOCALE;
  const timeZone = isValidTimeZone(options.timeZone) ? options.timeZone : resolveSessionTimeZone();
  const intlOptions = { ...(options.intl ?? {}), timeZone };

  return new Intl.DateTimeFormat(locale, intlOptions).format(date);
}

function normalizeApiUtcValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (hasExplicitTimeZone(trimmed)) return trimmed;
  return `${trimmed}Z`;
}

function hasExplicitTimeZone(value: string): boolean {
  return /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(value);
}

function readSessionTimeZoneFromProfile(): string {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as UserProfilePreferences;
    return typeof parsed.timezone === 'string' ? parsed.timezone.trim() : '';
  } catch {
    return '';
  }
}

function readBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function isValidTimeZone(value: string | null | undefined): value is string {
  if (!value || !value.trim()) return false;
  try {
    // Intl lança RangeError para timezones invalidos.
    new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}
